/* eslint-disable max-statements */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@nimbus-ds/components';
import {
  CreateDocumentRequestDto,
  GetDocumentSettingsResponseDto,
  InformationForContentDeclarationResponseDto,
  InformationForDocumentResponseDto,
} from '@tiendanube/common';
import { useNavegate, useQuery } from 'App/hooks';
import {
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  Stack,
  useResponsive,
} from 'commons/components';
import { useHasUnifiedShippingDocumentsEnabled } from 'domains/Auth/hooks';
import {
  trackingShippingPrintDocumentCancelButtonClick,
  trackingShippingPrintDocumentConfirmationButtonClick,
  trackingShippingPrintDocumentPageView,
} from 'domains/FulfillmentOrders/tracking';
import useTranslationFulfillmentOrders from 'domains/FulfillmentOrders/useTranslationFulfillmentOrders';
import {
  PrintDocumentForm,
  PrintDocumentPageError,
  PrintDocumentPreview,
} from './components';
import AlertNuvemEnvio from './components/AlertNuvemEnvio';
import AlertOcaLabelsWithError from './components/AlertOcaLabelsWithError';
import CardRegenerateOcaLabels from './components/CardRegenerateOcaLabels';
import {
  DOCUMENT_SETTINGS_EMPTY_STATE,
  DocumentType,
  FULFILLMENT_ORDER_ID_IDENTIFIER,
} from './constants';
import {
  useCreateDocumentsDownload,
  useGetInformationForDocument,
} from './hooks';
import useDocumentSettings from './hooks/useDocumentsSettings/useDocumentsSettings';
import useGetOcaLabels from './hooks/useGetOcaLabels';
import {
  defineIfShouldShowAlertNuvemEnvioLabel,
  getRequestByLocationIdForContentDeclaration,
  getRequestByLocationIdForDocument,
} from './utils';

interface PrintDocumentPageProps {
  readonly type: DocumentType;
}

function PrintDocumentPage({ type }: PrintDocumentPageProps) {
  const t = useTranslationFulfillmentOrders('printManager');
  const { isMobile } = useResponsive();
  const { goBack } = useNavegate();
  const query = useQuery();
  const selectedIds = query.get('ids')
    ? decodeURIComponent(query.get('ids')!).split(',')
    : [];
  const isUnifiedShippingDocumentsEnabled =
    useHasUnifiedShippingDocumentsEnabled();

  const {
    fetchInformationForDocument,
    information,
    isError,
    isLoading,
    isSuccess,
  } = useGetInformationForDocument(type);

  const {
    getOcaLabels,
    isLoadingLabels,
    isErrorLabels,
    mustWaitGeneration,
    labels,
    progress,
  } = useGetOcaLabels();

  const shouldShowAlertNuvemEnvioLabel = useMemo(
    () => defineIfShouldShowAlertNuvemEnvioLabel(type, information),
    [information, type],
  );

  const ocaLabelsWithError = labels
    .filter((label) => !!label.error || (!label.error && !label.html))
    .map((label) => label.externalId);

  const { documentSettings, fetchDocumentSettings } = useDocumentSettings();
  const { handleDownloadDocumentAsync, isGenerating } =
    useCreateDocumentsDownload();

  const hasOca = !!query.get('oca');
  const fetchParams: CreateDocumentRequestDto = selectedIds.reduce(
    (acc: CreateDocumentRequestDto, id) => {
      if (id.includes(FULFILLMENT_ORDER_ID_IDENTIFIER)) {
        acc.fulfillmentOrders?.push(
          id.replace(FULFILLMENT_ORDER_ID_IDENTIFIER, ''),
        );
      } else {
        acc.orders?.push(id);
      }
      return acc;
    },
    { fulfillmentOrders: [], orders: [] },
  );

  const [settings, setSettings] = useState<GetDocumentSettingsResponseDto>({
    ...(documentSettings ?? DOCUMENT_SETTINGS_EMPTY_STATE),
  });
  const [locationId, setLocationId] = useState<string>('');
  const handlePrint = useCallback(() => {
    window.print();
    trackingShippingPrintDocumentConfirmationButtonClick(
      type,
      'print',
      settings,
    );
  }, [settings, type]);

  const shouldBePrinted = useMemo(
    () =>
      (!settings?.downloadDocument &&
        type !== DocumentType.CONTENT_DECLARATION &&
        !isMobile) ||
      hasOca,
    [settings, type, isMobile, hasOca],
  );

  const handleDownload = useCallback(async () => {
    if (shouldBePrinted) {
      handlePrint();
      return;
    }

    const { fulfillmentOrders, orders } =
      type === DocumentType.CONTENT_DECLARATION
        ? getRequestByLocationIdForContentDeclaration({
            informationForContentDeclaration:
              information as InformationForContentDeclarationResponseDto[],
            locationId,
          })
        : getRequestByLocationIdForDocument({
            informationForDocument:
              information as InformationForDocumentResponseDto[],
            locationId,
            type,
          });

    trackingShippingPrintDocumentConfirmationButtonClick(
      type,
      'download',
      settings,
    );

    handleDownloadDocumentAsync({
      type,
      params: {
        fulfillmentOrders: fulfillmentOrders,
        orders: orders,
        settings: settings,
      },
    });
  }, [
    handleDownloadDocumentAsync,
    handlePrint,
    information,
    locationId,
    settings,
    shouldBePrinted,
    type,
  ]);

  const handleGoBack = () => {
    trackingShippingPrintDocumentCancelButtonClick(type, 'header');
    goBack();
  };

  const onRetryError = () => {
    fetchInformationForDocument(fetchParams);
    if (type !== DocumentType.CONTENT_DECLARATION) {
      fetchDocumentSettings();
    }
  };

  const pageType =
    type === DocumentType.LABEL && hasOca && isUnifiedShippingDocumentsEnabled
      ? 'ocaLabel'
      : type;

  useEffect(() => {
    fetchInformationForDocument(fetchParams);
    fetchDocumentSettings();
    trackingShippingPrintDocumentPageView(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !mustWaitGeneration &&
      hasOca &&
      labels.length === 0 &&
      !isLoadingLabels
    ) {
      getOcaLabels(fetchParams.orders ?? []);
    }
  });

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        <HeaderTop
          navigation={{
            onClick: handleGoBack,
          }}
        />
      }
      headerContent={
        <HeaderContent
          title={t(`${pageType}.title`)}
          subtitle={t(`${pageType}.subtitle`)}
        />
      }
    >
      <Stack align="baseline" justify="center" wrap={isMobile}>
        {(isError ||
          (isSuccess && information?.length === 0) ||
          (hasOca && isErrorLabels)) && (
          <PrintDocumentPageError onRetryError={onRetryError} />
        )}
        {!(isError || (isSuccess && information?.length === 0)) && (
          <>
            <Box width="100%" gap="4" flexDirection="column" display="flex">
              {shouldShowAlertNuvemEnvioLabel && (
                <AlertNuvemEnvio externalSaleId={selectedIds} />
              )}
              <PrintDocumentForm
                type={type}
                information={information}
                locationId={locationId}
                settings={settings}
                isPrinting={isGenerating}
                setSettings={setSettings}
                setLocationId={setLocationId}
                handleDownload={handleDownload}
                hasOca={hasOca}
              />
              {ocaLabelsWithError.length > 0 &&
                information &&
                information.length > 0 && (
                  <AlertOcaLabelsWithError
                    externalIds={ocaLabelsWithError}
                    information={(
                      information as InformationForDocumentResponseDto[]
                    ).filter(({ order }) =>
                      ocaLabelsWithError.includes(order.id),
                    )}
                  />
                )}
              {hasOca &&
                information &&
                information.length > 0 &&
                labels.length > 0 &&
                ocaLabelsWithError.length === 0 && (
                  <CardRegenerateOcaLabels externalIds={selectedIds} />
                )}
            </Box>
            <PrintDocumentPreview
              type={type}
              hasOca={hasOca}
              information={information}
              isLoading={isLoading || (hasOca && isLoadingLabels)}
              ocaLabels={labels}
              progressOca={progress}
              settings={settings}
              locationId={locationId}
            />
          </>
        )}
      </Stack>
    </IonPageStratus>
  );
}

export default PrintDocumentPage;
